/**
 * Mock data for the Column Mapping Review page.
 *
 * Mixed-direction model:
 *  - source_to_target rows: source column is static, target is a searchable dropdown
 *  - target_to_source rows: target field is static, source is a searchable dropdown
 *    (used in Step 1 for mandatory targets that have no auto-matched source column)
 *
 * Intentional demo errors in Step 1:
 *  - 3 mandatory targets have no source column: Insured Name, Risk Location Country, Class of Business
 *    (shown as target_to_source rows with empty sourceColumn)
 *  - 1 duplicate target: "Gross Premium" and "Net_Prem" both map to "Premium Amount" (f-08)
 */

import type { SchemaField, MappingRow, MappingStatus } from "@/types";

// ── Lloyd's 5.2 Target Schema Fields ─────────────────────────

export const LLOYDS_52_SCHEMA: SchemaField[] = [
  // ─── Mandatory Fields ───
  {
    id: "f-01",
    fieldName: "Unique Market Reference",
    definition: "The unique reference number assigned to the contract by the Lloyd's market",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-02",
    fieldName: "Year of Account",
    definition: "The year of account to which the risk attaches",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "integer",
  },
  {
    id: "f-03",
    fieldName: "Coverholder / DCA Name",
    definition: "Full registered name of the coverholder or delegated claims administrator",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-04",
    fieldName: "Risk Inception Date",
    definition: "Date on which coverage for the individual risk commences",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "date",
  },
  {
    id: "f-05",
    fieldName: "Risk Expiry Date",
    definition: "Date on which coverage for the individual risk ends",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "date",
  },
  {
    id: "f-06",
    fieldName: "Original Currency",
    definition: "The ISO 4217 currency code in which the risk was originally written",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-07",
    fieldName: "Total Sum Insured",
    definition: "The total insured value of the risk in the original currency",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "decimal",
  },
  {
    id: "f-08",
    fieldName: "Premium Amount",
    definition: "Gross premium amount in the original currency",
    fieldCategory: "Premiums",
    mandatory: true,
    dataType: "decimal",
  },
  {
    id: "f-09",
    fieldName: "Policy Number",
    definition: "The unique policy number assigned to the risk by the coverholder",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-10",
    fieldName: "Insured Name",
    definition: "Full legal name of the insured party",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-11",
    fieldName: "Risk Location Country",
    definition: "ISO 3166-1 alpha-2 country code of the primary risk location",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },
  {
    id: "f-12",
    fieldName: "Class of Business",
    definition: "Lloyd's class of business code identifying the type of insurance",
    fieldCategory: "Risk",
    mandatory: true,
    dataType: "string",
  },

  // ─── Additional Fields ───
  {
    id: "f-13",
    fieldName: "Branch",
    definition: "Branch or office responsible for the risk",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-14",
    fieldName: "Branch Reference",
    definition: "Internal reference number for the branch",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-15",
    fieldName: "Broker",
    definition: "Name of the placing broker",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-16",
    fieldName: "Broker Reference",
    definition: "Broker's own reference number for the placement",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-17",
    fieldName: "Certificate Number",
    definition: "Individual certificate or declaration number under the binding authority",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-18",
    fieldName: "Transaction Date",
    definition: "Date on which the transaction was recorded",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "date",
  },
  {
    id: "f-19",
    fieldName: "Risk State / Province",
    definition: "State or province of the primary risk location",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-20",
    fieldName: "Risk City",
    definition: "City of the primary risk location",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-21",
    fieldName: "Risk Postcode",
    definition: "Postal / ZIP code of the primary risk location",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-22",
    fieldName: "Risk Address",
    definition: "Full street address of the primary risk location",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-23",
    fieldName: "Commission Rate",
    definition: "Commission percentage payable to the coverholder",
    fieldCategory: "Premiums",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-24",
    fieldName: "Commission Amount",
    definition: "Calculated commission in original currency",
    fieldCategory: "Premiums",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-25",
    fieldName: "Net Premium",
    definition: "Premium net of commission and brokerage in original currency",
    fieldCategory: "Premiums",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-26",
    fieldName: "Tax Amount",
    definition: "Insurance premium tax or levy amount",
    fieldCategory: "Premiums",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-27",
    fieldName: "Deductible Amount",
    definition: "The deductible or excess applicable to the risk",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-28",
    fieldName: "Limit of Liability",
    definition: "Maximum amount payable under the policy per occurrence",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-29",
    fieldName: "Syndicate Number",
    definition: "The Lloyd's syndicate number underwriting the risk",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-30",
    fieldName: "Insured Address",
    definition: "Full mailing address of the insured party",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-31",
    fieldName: "Insured Country",
    definition: "Country of domicile of the insured party (ISO alpha-2)",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-32",
    fieldName: "Endorsement Reference",
    definition: "Reference number for any endorsements or amendments to the original risk",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-33",
    fieldName: "Date of Birth",
    definition: "Date of birth of the insured (personal lines)",
    fieldCategory: "Risk",
    mandatory: false,
    dataType: "date",
  },
  {
    id: "f-34",
    fieldName: "Claim Number",
    definition: "Unique claim reference number assigned by the coverholder",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-35",
    fieldName: "Claim Status",
    definition: "Current status of the claim (Open, Closed, Reopened)",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "string",
  },
  {
    id: "f-36",
    fieldName: "Loss Date",
    definition: "Date on which the loss occurred",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "date",
  },
  {
    id: "f-37",
    fieldName: "Paid Amount",
    definition: "Total amount paid to date on the claim",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-38",
    fieldName: "Outstanding Reserve",
    definition: "Current outstanding reserve amount for the claim",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-39",
    fieldName: "Incurred Amount",
    definition: "Total incurred amount (paid + outstanding) for the claim",
    fieldCategory: "Claims",
    mandatory: false,
    dataType: "decimal",
  },
  {
    id: "f-40",
    fieldName: "Settlement Currency",
    definition: "Currency in which the settlement is made (ISO 4217)",
    fieldCategory: "Premiums",
    mandatory: false,
    dataType: "string",
  },
];

export const MANDATORY_FIELDS = LLOYDS_52_SCHEMA.filter((f) => f.mandatory);
export const ADDITIONAL_FIELDS = LLOYDS_52_SCHEMA.filter((f) => !f.mandatory);

/** All target field names for the SearchableSelect dropdown */
export const TARGET_OPTIONS = LLOYDS_52_SCHEMA.map((f) => f.fieldName);

/** Target options excluding mandatory fields (for additional fields tab) */
export const ADDITIONAL_TARGET_OPTIONS = ADDITIONAL_FIELDS.map((f) => f.fieldName);

/** Set of mandatory field IDs for quick lookup */
export const MANDATORY_FIELD_IDS = new Set(MANDATORY_FIELDS.map((f) => f.id));

/** Lookup helpers */
export const SCHEMA_BY_ID = new Map(LLOYDS_52_SCHEMA.map((f) => [f.id, f]));
export const SCHEMA_BY_NAME = new Map(LLOYDS_52_SCHEMA.map((f) => [f.fieldName, f]));

export function getTargetFieldName(id: string): string {
  return SCHEMA_BY_ID.get(id)?.fieldName ?? "";
}

export function getSchemaByName(name: string): SchemaField | undefined {
  return SCHEMA_BY_NAME.get(name);
}

// ── Mock Source Columns (from uploaded BDX file) ────────────

export const MOCK_SOURCE_COLUMNS = [
  "UMR",
  "YOA",
  "Coverholder",
  "Inception",
  "Expiry",
  "Ccy",
  "TSI",
  "Gross Premium",
  "Pol_No",
  "Insured",
  "Country",
  "CoB_Code",
  "Branch_Office",
  "Branch_Ref",
  "Broker_Name",
  "Broker_Ref",
  "Cert_No",
  "Trans_Date",
  "State_Province",
  "City",
  "Postcode",
  "Address_Line_1",
  "Commission_%",
  "Comm_Amount",
  "Net_Prem",
  "Tax",
  "Deductible",
  "Limit",
  "Syndicate",
  "Insured_Addr",
  "Insured_Country",
  "Endorsement_Ref",
  "DOB",
  "Claim_Ref",
  "Claim_Status",
  "Loss_Date_Field",
  "Amount_Paid",
  "Reserve",
  "Incurred_Total",
  "Settlement_Ccy",
  "Internal_ID",
  "Processed_By",
  "Upload_Batch",
  "Notes",
];

// ── Stable ID generator ──────────────────────────────────────

let _counter = 0;
function makeId() {
  return `map-${++_counter}`;
}

// ── Mock Mapping Generators ──────────────────────────────────

/**
 * Step 1: Mandatory target coverage.
 *
 * Two row types:
 *  - source_to_target: Source columns the ML pipeline matched to mandatory targets.
 *    Source is static text, target is a dropdown the user can change.
 *  - target_to_source: Mandatory targets with NO auto-matched source column.
 *    Target is static text, source is a dropdown of uploaded file headers.
 *
 * Demo errors:
 *  - 3 target_to_source rows with empty sourceColumn (Insured Name, Risk Location Country, Class of Business)
 *  - "Gross Premium" AND "Net_Prem" both map to "Premium Amount" (f-08) — duplicate target
 */
export function generateMandatoryMappings(): MappingRow[] {
  _counter = 0;
  return [
    // ─── source_to_target: ML-matched source columns ───
    {
      id: makeId(),
      sourceColumn: "UMR",
      targetFieldId: "f-01",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-01"],
    },
    {
      id: makeId(),
      sourceColumn: "YOA",
      targetFieldId: "f-02",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-02"],
    },
    {
      id: makeId(),
      sourceColumn: "Coverholder",
      targetFieldId: "f-03",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-03", "f-15"],
    },
    {
      id: makeId(),
      sourceColumn: "Inception",
      targetFieldId: "f-04",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.91,
      approved: false,
      suggestions: ["f-04", "f-05", "f-18"],
    },
    {
      id: makeId(),
      sourceColumn: "Expiry",
      targetFieldId: "f-05",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.89,
      approved: false,
      suggestions: ["f-05", "f-04"],
    },
    {
      id: makeId(),
      sourceColumn: "Ccy",
      targetFieldId: "f-06",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-06", "f-40"],
    },
    {
      id: makeId(),
      sourceColumn: "TSI",
      targetFieldId: "f-07",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.87,
      approved: false,
      suggestions: ["f-07", "f-28"],
    },
    // DUPLICATE: both "Gross Premium" and "Net_Prem" target "Premium Amount" (f-08)
    {
      id: makeId(),
      sourceColumn: "Gross Premium",
      targetFieldId: "f-08",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-08", "f-25"],
    },
    {
      id: makeId(),
      sourceColumn: "Pol_No",
      targetFieldId: "f-09",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-09", "f-17"],
    },
    {
      id: makeId(),
      sourceColumn: "Net_Prem",
      targetFieldId: "f-08",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.72,
      approved: false,
      suggestions: ["f-08", "f-25"],
    },

    // ─── target_to_source: Mandatory targets with no auto-match ───
    // User must pick which source column maps to each of these.
    // Suggestions here are source column names (not field IDs).
    {
      id: makeId(),
      sourceColumn: "",
      targetFieldId: "f-10",
      direction: "target_to_source",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: ["Insured", "Coverholder", "Broker_Name"],
    },
    {
      id: makeId(),
      sourceColumn: "",
      targetFieldId: "f-11",
      direction: "target_to_source",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: ["Country", "Insured_Country", "State_Province"],
    },
    {
      id: makeId(),
      sourceColumn: "",
      targetFieldId: "f-12",
      direction: "target_to_source",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: ["CoB_Code", "Branch_Office"],
    },
  ];
}

/**
 * Step 2: Additional target field mappings + unmapped source columns.
 * All rows are source_to_target direction (source static, target dropdown).
 */
export function generateAdditionalMappings(): MappingRow[] {
  return [
    {
      id: makeId(),
      sourceColumn: "Branch_Office",
      targetFieldId: "f-13",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-13"],
    },
    {
      id: makeId(),
      sourceColumn: "Branch_Ref",
      targetFieldId: "f-14",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-14"],
    },
    {
      id: makeId(),
      sourceColumn: "Broker_Name",
      targetFieldId: "f-15",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-15", "f-03"],
    },
    {
      id: makeId(),
      sourceColumn: "Broker_Ref",
      targetFieldId: "f-16",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-16"],
    },
    {
      id: makeId(),
      sourceColumn: "Cert_No",
      targetFieldId: "f-17",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.82,
      approved: false,
      suggestions: ["f-17", "f-09"],
    },
    {
      id: makeId(),
      sourceColumn: "Trans_Date",
      targetFieldId: "f-18",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.78,
      approved: false,
      suggestions: ["f-18", "f-04", "f-36"],
    },
    {
      id: makeId(),
      sourceColumn: "State_Province",
      targetFieldId: "f-19",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.93,
      approved: false,
      suggestions: ["f-19", "f-20"],
    },
    {
      id: makeId(),
      sourceColumn: "City",
      targetFieldId: "f-20",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.88,
      approved: false,
      suggestions: ["f-20", "f-19"],
    },
    {
      id: makeId(),
      sourceColumn: "Postcode",
      targetFieldId: "f-21",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-21"],
    },
    {
      id: makeId(),
      sourceColumn: "Address_Line_1",
      targetFieldId: "f-22",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.84,
      approved: false,
      suggestions: ["f-22", "f-30"],
    },
    {
      id: makeId(),
      sourceColumn: "Commission_%",
      targetFieldId: "f-23",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-23"],
    },
    {
      id: makeId(),
      sourceColumn: "Comm_Amount",
      targetFieldId: "f-24",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-24"],
    },
    {
      id: makeId(),
      sourceColumn: "Tax",
      targetFieldId: "f-26",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.75,
      approved: false,
      suggestions: ["f-26", "f-24"],
    },
    {
      id: makeId(),
      sourceColumn: "Deductible",
      targetFieldId: "f-27",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.81,
      approved: false,
      suggestions: ["f-27", "f-28"],
    },
    {
      id: makeId(),
      sourceColumn: "Limit",
      targetFieldId: "f-28",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.86,
      approved: false,
      suggestions: ["f-28", "f-07", "f-27"],
    },
    {
      id: makeId(),
      sourceColumn: "Syndicate",
      targetFieldId: "f-29",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-29"],
    },
    {
      id: makeId(),
      sourceColumn: "Insured_Addr",
      targetFieldId: "f-30",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.77,
      approved: false,
      suggestions: ["f-30", "f-22"],
    },
    {
      id: makeId(),
      sourceColumn: "Insured_Country",
      targetFieldId: "f-31",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.9,
      approved: false,
      suggestions: ["f-31", "f-11"],
    },
    {
      id: makeId(),
      sourceColumn: "Endorsement_Ref",
      targetFieldId: "f-32",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-32"],
    },
    {
      id: makeId(),
      sourceColumn: "DOB",
      targetFieldId: "f-33",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-33"],
    },
    {
      id: makeId(),
      sourceColumn: "Claim_Ref",
      targetFieldId: "f-34",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-34"],
    },
    {
      id: makeId(),
      sourceColumn: "Claim_Status",
      targetFieldId: "f-35",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-35"],
    },
    {
      id: makeId(),
      sourceColumn: "Loss_Date_Field",
      targetFieldId: "f-36",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.87,
      approved: false,
      suggestions: ["f-36", "f-18", "f-04"],
    },
    {
      id: makeId(),
      sourceColumn: "Amount_Paid",
      targetFieldId: "f-37",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.92,
      approved: false,
      suggestions: ["f-37", "f-38", "f-39"],
    },
    {
      id: makeId(),
      sourceColumn: "Reserve",
      targetFieldId: "f-38",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.88,
      approved: false,
      suggestions: ["f-38", "f-37"],
    },
    {
      id: makeId(),
      sourceColumn: "Incurred_Total",
      targetFieldId: "f-39",
      direction: "source_to_target",
      status: "ML",
      confidence: 0.91,
      approved: false,
      suggestions: ["f-39", "f-37", "f-38"],
    },
    {
      id: makeId(),
      sourceColumn: "Settlement_Ccy",
      targetFieldId: "f-40",
      direction: "source_to_target",
      status: "known_mapping",
      confidence: 1.0,
      approved: true,
      suggestions: ["f-40", "f-06"],
    },
    // Unmapped source columns
    {
      id: makeId(),
      sourceColumn: "Internal_ID",
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: [],
    },
    {
      id: makeId(),
      sourceColumn: "Processed_By",
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: [],
    },
    {
      id: makeId(),
      sourceColumn: "Upload_Batch",
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: [],
    },
    {
      id: makeId(),
      sourceColumn: "Notes",
      targetFieldId: "",
      direction: "source_to_target",
      status: "needs_match",
      confidence: 0,
      approved: false,
      suggestions: [],
    },
  ];
}

/** Status display config */
export const STATUS_CONFIG: Record<
  MappingStatus,
  { label: string; bg: string; text: string; border: string }
> = {
  known_mapping: {
    label: "Known Mapping",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  ML: {
    label: "ML",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  LLM: {
    label: "LLM",
    bg: "bg-purple-50",
    text: "text-purple-700",
    border: "border-purple-200",
  },
  needs_match: {
    label: "Needs Match",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
  },
  mapped_by_user: {
    label: "Mapped by User",
    bg: "bg-sky-50",
    text: "text-sky-700",
    border: "border-sky-200",
  },
};

/** Sort priority for statuses (needs_match first so user sees errors) */
export const STATUS_SORT_ORDER: Record<MappingStatus, number> = {
  needs_match: 0,
  LLM: 1,
  ML: 2,
  mapped_by_user: 3,
  known_mapping: 4,
};
